import { Controller } from "@hotwired/stimulus"

// Full-screen PDF viewer (ERP::PdfViewer::Component). Opening fetches the PDF and shows it from a blob URL: a PDF
// the server refuses comes back as a redirect to an HTML page, which shows an error instead of loading that page
// in the frame, and keeps the download link hidden. The PDF is released when the dialog closes, and before Turbo
// caches the page.
// Connects to data-controller="erp--pdf-viewer"
export default class extends Controller {
  static targets = ["dialog", "frame", "status", "error", "download"]
  static values = { url: String }

  connect() {
    this._onBeforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    this.close()
    this.unload()
  }

  async open(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) return

    this.dialogTarget.showModal()
    this.#show(this.statusTarget)
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/pdf" } })
      const type = response.headers.get("Content-Type") || ""
      if (!response.ok || !type.includes("application/pdf")) throw new Error(`PDF unavailable (${response.status})`)

      const blob = await response.blob()
      if (!this.dialogTarget.open) return

      this.unload()
      this.objectUrl = URL.createObjectURL(blob)
      this.frameTarget.src = this.objectUrl
      this.#show(this.frameTarget)
    } catch {
      this.#show(this.errorTarget)
    }
  }

  close(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  // On the dialog's close event, whatever closed it (button, Escape)
  unload() {
    this.frameTarget.removeAttribute("src")
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }

  // Hands over to the send dialog of the page
  send({ params: { dialog } }) {
    this.close()
    document.getElementById(dialog)?.showModal()
  }

  #show(target) {
    [this.statusTarget, this.errorTarget, this.frameTarget].forEach((element) => { element.hidden = element !== target })
    this.downloadTarget.hidden = target !== this.frameTarget
  }
}
